import {
	FolderIcon,
	InboxIcon,
	PinIcon,
	PlusIcon,
	StarIcon
} from "lucide-react"
import { ListSection } from "~/components/SectionHeader"
import { Button } from "~/components/ui/button"
import { ScrollArea } from "~/components/ui/scroll-area"
import { compareNames } from "~/lib/utils"
import type { RouterOutputs } from "~/trpc/react"
import { KBDocumentRow } from "./KBDocumentRow"

export function KBDocumentList({
	documents,
	onCreate
}: {
	documents: RouterOutputs["knowledgeBase"]["list"]
	onCreate?: (category?: string | null) => void
}) {
	const sortedDocuments = [...documents].sort((a, b) =>
		compareNames({ id: a.id, name: a.title }, { id: b.id, name: b.title })
	)
	const regularDocuments = sortedDocuments.filter(
		(doc) => !doc.pinned && !doc.userHasFavorited
	)
	const groupedDocuments = regularDocuments.reduce<
		Record<string, typeof regularDocuments>
	>((acc, document) => {
		const category = document.category?.trim() || "Uncategorized"
		acc[category] = [...(acc[category] ?? []), document]
		return acc
	}, {})

	const categories = Object.keys(groupedDocuments).sort((a, b) =>
		compareNames({ id: a, name: a }, { id: b, name: b })
	)

	const pinnedDocuments = sortedDocuments.filter((doc) => doc.pinned)
	const userFavouritedDocuments = sortedDocuments.filter(
		(doc) => doc.userHasFavorited
	)

	const createButton = (category?: string | null) =>
		onCreate ? (
			<Button variant="outline" size="sm" onClick={() => onCreate(category)}>
				<PlusIcon className="size-4" />
			</Button>
		) : null

	return (
		<ScrollArea className="h-full">
			<div className="flex w-full flex-col gap-4">
				{pinnedDocuments.length > 0 && (
					<ListSection
						variant="raised"
						headerVariant="solid"
						itemName="page"
						icon={<PinIcon className="size-4" />}
						title="Pinned"
						count={pinnedDocuments.length}
						action={createButton(null)}
					>
						{pinnedDocuments.map((document) => (
							<KBDocumentRow key={document.id} document={document} />
						))}
					</ListSection>
				)}
				{userFavouritedDocuments.length > 0 && (
					<ListSection
						variant="raised"
						headerVariant="solid"
						itemName="page"
						icon={<StarIcon className="size-4 text-yellow-300" />}
						title="My Favorites"
						count={userFavouritedDocuments.length}
						action={createButton(null)}
					>
						{userFavouritedDocuments.map((document) => (
							<KBDocumentRow key={document.id} document={document} />
						))}
					</ListSection>
				)}
				{categories.length ? (
					categories.map((category) => (
						<ListSection
							variant="raised"
							headerVariant="solid"
							itemName="page"
							key={category}
							icon={<FolderIcon className="size-4" />}
							title={category}
							count={groupedDocuments[category]?.length ?? 0}
							action={createButton(
								category === "Uncategorized" ? null : category
							)}
						>
							{groupedDocuments[category]?.map((document) => (
								<KBDocumentRow key={document.id} document={document} />
							))}
						</ListSection>
					))
				) : (
					<ListSection
						variant="raised"
						headerVariant="solid"
						itemName="page"
						icon={<InboxIcon className="size-4" />}
						title="No documents yet"
						count={0}
						action={createButton(null)}
					>
						<div className="flex min-h-28 items-center justify-center px-4 py-8 text-sm font-medium text-text-sub">
							No documents found.
						</div>
					</ListSection>
				)}
			</div>
		</ScrollArea>
	)
}
